import React, { useMemo, useState } from "react";
import { SPECIALITES } from "./constants/specialites";
import { AddressAutocomplete } from "./components/AddressAutocomplete";
import { useLocation, useNavigate } from "react-router-dom";
import "./styles/Login.css";
import logo from "./assets/logo.png";
import { API_BASE } from "./config";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/* --- Inline SVG icons --- */
function Svg({ children, ...props }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="i" {...props}>
      {children}
    </svg>
  );
}

const Icons = {
  calendar: (p) => (
    <Svg {...p}>
      <path d="M7 2v3M17 2v3" />
      <path d="M3.5 9h17" />
      <path d="M6 6h12a2.5 2.5 0 0 1 2.5 2.5v11A2.5 2.5 0 0 1 18 22H6A2.5 2.5 0 0 1 3.5 19.5v-11A2.5 2.5 0 0 1 6 6z" />
    </Svg>
  ),
  stetho: (p) => (
    <Svg {...p}>
      <path d="M6 3v5a4 4 0 0 0 8 0V3" />
      <path d="M10 12v2a4.5 4.5 0 0 0 9 0v-2" />
      <path d="M19 10a2 2 0 1 0 0 4" />
      <path d="M6 3h2M14 3h2" />
    </Svg>
  ),
  building: (p) => (
    <Svg {...p}>
      <path d="M4.5 21V4.5A2.5 2.5 0 0 1 7 2h10a2.5 2.5 0 0 1 2.5 2.5V21" />
      <path d="M9 6h2M13 6h2M9 10h2M13 10h2M9 14h2M13 14h2" />
      <path d="M10 21v-4h4v4" />
    </Svg>
  ),
  mail: (p) => (
    <Svg {...p}>
      <path d="M4 6h16v12H4z" />
      <path d="M4 7l8 6 8-6" />
    </Svg>
  ),
  lock: (p) => (
    <Svg {...p}>
      <path d="M7 11V8a5 5 0 0 1 10 0v3" />
      <path d="M6 11h12v10H6z" />
    </Svg>
  ),
  user: (p) => (
    <Svg {...p}>
      <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4z" />
      <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
    </Svg>
  ),
  phone: (p) => (
    <Svg {...p}>
      <path d="M7 3h3l2 5-2 1c1.2 2.6 3.4 4.8 6 6l1-2 5 2v3c0 1.1-.9 2-2 2-9.4 0-17-7.6-17-17 0-1.1.9-2 2-2z" />
    </Svg>
  ),
  pin: (p) => (
    <Svg {...p}>
      <path d="M12 21s7-5 7-11a7 7 0 1 0-14 0c0 6 7 11 7 11z" />
      <path d="M12 10.5a2.2 2.2 0 1 0-2.2-2.2 2.2 2.2 0 0 0 2.2 2.2z" />
    </Svg>
  ),
  eye: (p) => (
    <Svg {...p}>
      <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7z" />
      <path d="M12 15.2A3.2 3.2 0 1 0 8.8 12 3.2 3.2 0 0 0 12 15.2z" />
    </Svg>
  ),
};

function RoleCard({ active, title, subtitle, icon, onClick }) {
  return (
    <button
      type="button"
      className={`role ${active ? "active" : ""}`}
      onClick={onClick}
    >
      <div className="roleIconBox">{icon}</div>
      <div className="roleTitle">{title}</div>
      <div className="roleSub">{subtitle}</div>
    </button>
  );
}

function Field({ label, icon, right, ...props }) {
  return (
    <label className="field">
      <div className="fieldLabel">{label}</div>
      <div className="input">
        <span className="inputLeft">{icon}</span>
        <input className="inputControl" {...props} />
        {right ? <span className="inputRight">{right}</span> : null}
      </div>
    </label>
  );
}

function SelectField({ label, icon, right, value, onChange, children }) {
  return (
    <label className="field">
      <div className="fieldLabel">{label}</div>
      <div className="input">
        <span className="inputLeft">{icon}</span>
        <select className="inputControl" value={value} onChange={onChange}>
          {children}
        </select>
        {right ? <span className="inputRight">{right}</span> : null}
      </div>
    </label>
  );
}

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const q = useQuery();

  const initialRole =
    q.get("role") === "collectivite" ? "GESTIONNAIRE" :
    q.get("role") === "medecin"      ? "MEDECIN"      :
    "USAGER";
  const [role, setRole] = useState(initialRole);

  const [mode, setMode] = useState("login"); // login | register
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);


  // Shared (login/register)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register MEDECIN
  const [nomComplet, setNomComplet] = useState("");
  const [agrement, setAgrement] = useState("ARS");
  const [agrementConfirmed, setAgrementConfirmed] = useState(false);
  const [autoriteAgrement, setAutoriteAgrement] = useState("");
  const [specialite, setSpecialite] = useState("Médecine générale");
  // Register USAGER
  const [nomUsager, setNomUsager]               = useState("");
  const [prenomUsager, setPrenomUsager]         = useState("");
  const [telephoneUsager, setTelephoneUsager]   = useState("");
  const [dateNaissanceUsager, setDateNaissanceUsager] = useState("");
  const [adresseUsager, setAdresseUsager]       = useState("");
  const [confirmPassword, setConfirmPassword]   = useState("");

  const [telephoneCabinet, setTelephoneCabinet] = useState("");
  const [adresseCabinet, setAdresseCabinet] = useState("");
  const [codePostalCabinet, setCodePostalCabinet] = useState("");
  const [villeCabinet, setVilleCabinet] = useState("");
  const [rpps, setRpps] = useState("");

  const [info, setInfo] = useState("");


  // Register GESTIONNAIRE
  const [nomCollectivite, setNomCollectivite] = useState("");
  const [adresseCollectivite, setAdresseCollectivite] = useState("");
  const [codePostalCollectivite, setCodePostalCollectivite] = useState("");
  const [villeCollectivite, setVilleCollectivite] = useState("");  const [telephoneCollectivite, setTelephoneCollectivite] = useState("");
  const [siret, setSiret] = useState("");

  const [error, setError] = useState("");
  
  const [success, setSuccess] = useState("");

  // Register step
  const [step, setStep] = useState("FORM"); // FORM | VERIFY
  const [pendingEmail, setPendingEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  // ⚠️ Mets la vraie route de vérification de ton backend si elle est différente
  const REGISTER_URL = `${API_BASE}/register`;
  const LOGIN_URL = `${API_BASE}/login`;
  const VERIFY_URL = `${API_BASE}/verify-email`;

  const redirectByRole = (r) => {
    if (r === "MEDECIN") navigate("/dashboard/medecin");
    else if (r === "GESTIONNAIRE") navigate("/dashboard/collectivite");
    else if (r === "SUPER_ADMIN") navigate("/dashboard/admin");
    else if (r === "USAGER") navigate("/dashboard/usager");
    else navigate("/login");
  };

  const resetMessages = () => {
    setError("");
    setSuccess("");
    setInfo("");
  };

  const resetRegisterFields = () => {
    setNomUsager("");
    setPrenomUsager("");
    setTelephoneUsager("");
    setDateNaissanceUsager("");
    setAdresseUsager("");
    setConfirmPassword("");
    setNomComplet("");
    setAgrement("ARS");
    setAgrementConfirmed(false);
    setAutoriteAgrement("");
    setSpecialite("Médecine générale");
    setTelephoneCabinet("");
    setAdresseCabinet("");
    setNomCollectivite("");
    setAdresseCollectivite("");
    setTelephoneCollectivite("");
    setSiret("");
    setPendingEmail("");
    setVerificationCode("");
    setAdresseCabinet("");
    setCodePostalCabinet("");
    setVilleCabinet("");
    setRpps("");
    setAdresseCollectivite("");
    setCodePostalCollectivite("");
    setVilleCollectivite("");


    setStep("FORM");
  };

  const handleSubmitLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
  
    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, role }),
      });
  
      const data = await res.json().catch(() => ({}));
  
      if (res.ok) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("user_role", data.role);
        localStorage.setItem("user_email", data.email);
  
        // attendre 2 secondes avant redirection
        setTimeout(() => {
          onLogin(data.access_token, data.role, data.email, data.nom_complet || "");
          redirectByRole(data.role);
        }, 1000);
  
      } else {
        setError(data.msg || "Erreur serveur");
        setLoading(false);
      }
  
    } catch {
      setError("Impossible de contacter le serveur.");
      setLoading(false);
    }
  };
  

  const handleSubmitRegister = async (e) => {
    e.preventDefault();
    resetMessages();
  
    if (loading) return;

    // Validation confirmation mot de passe (USAGER)
    if (role === "USAGER" && password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    // Validation confirmation agrément (MEDECIN hors Médecine générale)
    if (role === "MEDECIN" && specialite !== "Médecine générale" && !agrementConfirmed) {
      setError("Veuillez confirmer votre agrément avant de continuer.");
      return;
    }

    setLoading(true);

    const payload =
    role === "USAGER"
      ? {
          role,
          email:          email.trim(),
          password,
          nom:            nomUsager.trim(),
          prenom:         prenomUsager.trim(),
          telephone:      telephoneUsager.trim(),
          date_naissance: dateNaissanceUsager || null,
          adresse:        adresseUsager.trim(),
        }
    : role === "MEDECIN"
      ? {
          role,
          email: email.trim(),
          password,
          nom_complet: nomComplet.trim(),
          agrement,
          autorite_agrement: autoriteAgrement.trim(),
          specialite: specialite.trim(),
          rpps: rpps.trim(),
          telephone_cabinet: telephoneCabinet.trim(),
  
          // ✅ Adresse découpée
          adresse_cabinet: adresseCabinet.trim(),
          code_postal_cabinet: codePostalCabinet.trim(),
          ville_cabinet: villeCabinet.trim(),
        }
      : {
          role,
          email: email.trim(),
          password,
          collectivite_nom: nomCollectivite.trim(),
  
          // ✅ Adresse découpée
          collectivite_adresse: adresseCollectivite.trim(),
          collectivite_code_postal: codePostalCollectivite.trim(),
          collectivite_ville: villeCollectivite.trim(),
  
          collectivite_telephone: telephoneCollectivite.trim(),
          collectivite_siret: (siret || "").replace(/\D/g, ""),
        };
  
  
    try {
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload), // ✅ payload existe bien ici
      });
  
      const data = await res.json().catch(() => ({}));
  
      if (res.ok) {
        if (data.access_token) {
          const finalRole = data.role || role;
          onLogin(data.access_token, finalRole, data.email || email);
          redirectByRole(finalRole);
          return;
        }
  
        setPendingEmail((data.email || email).trim());
        setStep("VERIFY");
        setSuccess(data.msg || "Code envoyé. Vérifiez votre email.");
      } else {
        setError(data.msg || "Erreur lors de la création du compte");
      }
    } catch {
      setError("Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };
  
  

  const handleSubmitVerify = async (e) => {
    e.preventDefault();
    resetMessages();

    const emailToVerify = (pendingEmail || email).trim();

    if (!emailToVerify) {
      setError("Email manquant pour la vérification.");
      return;
    }

    if (!verificationCode.trim()) {
      setError("Veuillez saisir le code de vérification.");
      return;
    }

    try {
      const res = await fetch(VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailToVerify,
          code: verificationCode.trim(),
          role,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {

        // ✅ Cas 1 : connexion directe
        if (data.access_token) {
          const finalRole = data.role || role;
          onLogin(data.access_token, finalRole, data.email || emailToVerify, data.nom_complet || "");
          redirectByRole(finalRole);
          return;
        }
      
        // ✅ Cas 2 : en attente validation admin
        if (data.status === "PENDING_ADMIN_APPROVAL") {
          setInfo(data.msg);   // 🔴 on l'affiche en rouge/orange
          setMode("login");
          setStep("FORM");
          setVerificationCode("");
          return;
        }
      
        // ✅ Cas normal
        setSuccess(data.msg || "Email vérifié.");
      
      } else {
        setError(data.msg || "Code invalide");
      }
    } catch {
      setError("Impossible de contacter le serveur.");
    }
  };

  const switchToLogin = () => {
    resetMessages();
    setMode("login");
    setStep("FORM");
  };

  const switchToRegister = () => {
    resetMessages();
    setMode("register");
    setStep("FORM");
    setPendingEmail("");
    setVerificationCode("");
  };

  return (
    <div className="lp">
      {/* LEFT BLUE PANEL */}
      <aside className="lpLeft fade-in fade-delay-1">
        <div className="lpPattern" />
        <div className="lpLeftContent">
          <div className="lpLogoBox">
            <img src={logo} alt="FormiDoc" className="lpLogo" />
          </div>
          { /* <h1 className="lpTitle">FormiDoc</h1>*/}
          <p className="lpSubtitle">
  Plateforme intelligente de gestion des rendez-vous médicaux
</p>

          <div className="lpMiniCards">
            <div className="miniCard">
              <div className="miniIcon">
                {Icons.user({ className: "i iMD" })}
              </div>
              <div className="miniTitle">Usagers</div>
              <div className="miniSub">Accédez à vos RDV</div>
            </div>
            <div className="miniCard">
              <div className="miniIcon">
                {Icons.building({ className: "i iMD" })}
              </div>
              <div className="miniTitle">Collectivités</div>
              <div className="miniSub">Planifiez les RDV</div>
            </div>
            <div className="miniCard">
              <div className="miniIcon">
                {Icons.stetho({ className: "i iMD" })}
              </div>
              <div className="miniTitle">Professionnels</div>
              <div className="miniSub">Consultations & agenda</div>
            </div>
          </div>
        </div>
      </aside>

      {/* RIGHT PANEL */}
      <main className="lpRight">
      <div className="panel fade-in fade-delay-2">
          <div className="panelTop">
            <div className="panelHeading">
              
              {role === "MEDECIN" && mode === "register"
                ? "Sélectionnez votre profil"
                : role === "MEDECIN" && mode === "login"
                ? "Sélectionnez votre profil"
                : "Sélectionnez votre profil"}
            </div>

            <div className="rolesRow">
              <RoleCard
                active={role === "USAGER"}
                title="Usager"
                subtitle="Accédez à vos rendez-vous"
                icon={Icons.user({ className: "i iMD roleSvg" })}
                onClick={() => { setRole("USAGER"); resetMessages(); }}
              />
              <RoleCard
                active={role === "GESTIONNAIRE"}
                title="Collectivité"
                subtitle="Gérez bénéficiaires & RDV"
                icon={Icons.building({ className: "i iMD roleSvg" })}
                onClick={() => { setRole("GESTIONNAIRE"); resetMessages(); }}
              />
              <RoleCard
                active={role === "MEDECIN"}
                title="Professionnel"
                subtitle="Médecins et psychologues"
                icon={Icons.stetho({ className: "i iMD roleSvg" })}
                onClick={() => { setRole("MEDECIN"); resetMessages(); }}
              />
            </div>

            <div className="tabs">
              <button
                type="button"
                className={`tab ${mode === "login" ? "active" : ""}`}
                onClick={switchToLogin}
              >
                Connexion
              </button>
              <button
                type="button"
                className={`tab ${mode === "register" ? "active" : ""}`}
                onClick={switchToRegister}
              >
                Inscription
              </button>
            </div>
          </div>

          {(error || success || info) && (
                <div
                  className={`msg ${
                    error ? "msgError" :
                    info ? "msgWarning" :
                    "msgSuccess"
                  }`}
                >
                  {error || info || success}
                </div>
              )}


          <div className="panelBody fade-in fade-delay-3">
            {/* -------- LOGIN -------- */}
            {mode === "login" && (
              <form className="form" onSubmit={handleSubmitLogin}>
                <Field
                  label="Adresse email"
                  type="email"
                  placeholder="votre@email.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={Icons.mail({ className: "i iSM" })}
                  required
                />

                <Field
                  label="Mot de passe"
                  type={showPwd ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={Icons.lock({ className: "i iSM" })}
                  right={
                    <button
                      type="button"
                      className="eyeBtn"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={
                        showPwd
                          ? "Masquer le mot de passe"
                          : "Afficher le mot de passe"
                      }
                    >
                      {Icons.eye({ className: "i iSM" })}
                    </button>
                  }
                  required
                />

                <button
                  type="button"
                  className="ForgetPassword"
                  onClick={() => navigate("/forgot-password")}
                >
                  Mot de passe oublié ?
                </button>

                <button
                type="submit"
                className="primaryBtn"
                disabled={loading}
              >
                {loading ? "Connexion ..." : "Se connecter"}
              </button>

                <button
                  type="button"
                  className="superAdmin"
                  onClick={() => navigate("/")}
                >
                  ← Retour à l'accueil  
                </button>
              </form>
            )}

            {/* -------- REGISTER FORM -------- */}
            {mode === "register" && step === "FORM" && (
              <form className="form" onSubmit={handleSubmitRegister}>

                {/* Champs communs — cachés pour USAGER (gérés dans sa section) */}
                {role !== "USAGER" && (
                  <>
                    <Field
                      label="Adresse email"
                      type="email"
                      placeholder="votre@email.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      icon={Icons.mail({ className: "i iSM" })}
                      required
                    />
                    <Field
                      label="Mot de passe"
                      type={showPwd ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      icon={Icons.lock({ className: "i iSM" })}
                      right={
                        <button
                          type="button"
                          className="eyeBtn"
                          onClick={() => setShowPwd((v) => !v)}
                          aria-label={showPwd ? "Masquer" : "Afficher"}
                        >
                          {Icons.eye({ className: "i iSM" })}
                        </button>
                      }
                      required
                    />
                  </>
                )}

                {role === "USAGER" ? (
                  <>
                    {/* Nom + Prénom côte à côte */}
                    <div className="rowFields">
                      <Field
                        label="Nom"
                        type="text"
                        placeholder="Dupont"
                        value={nomUsager}
                        onChange={(e) => setNomUsager(e.target.value)}
                        icon={Icons.user({ className: "i iSM" })}
                        required
                      />
                      <Field
                        label="Prénom"
                        type="text"
                        placeholder="Jean"
                        value={prenomUsager}
                        onChange={(e) => setPrenomUsager(e.target.value)}
                        icon={Icons.user({ className: "i iSM" })}
                        required
                      />
                    </div>

                    <Field
                      label="Adresse email"
                      type="email"
                      placeholder="votre@email.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      icon={Icons.mail({ className: "i iSM" })}
                      required
                    />

                    <Field
                      label="Téléphone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={telephoneUsager}
                      onChange={(e) => setTelephoneUsager(e.target.value)}
                      icon={Icons.phone({ className: "i iSM" })}
                      required
                    />

                    <Field
                      label="Date de naissance"
                      type="date"
                      value={dateNaissanceUsager}
                      onChange={(e) => setDateNaissanceUsager(e.target.value)}
                      icon={Icons.calendar({ className: "i iSM" })}
                      required
                    />

                    <AddressAutocomplete
                      label="Adresse"
                      icon={Icons.pin({ className: "i iSM" })}
                      value={adresseUsager}
                      onChange={(e) => setAdresseUsager(e.target.value)}
                      onSelect={({ label }) => setAdresseUsager(label)}
                      placeholder="Ex : 10 rue de la Paix, 75001 Paris"
                    />

                    <Field
                      label="Mot de passe"
                      type={showPwd ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      icon={Icons.lock({ className: "i iSM" })}
                      right={
                        <button
                          type="button"
                          className="eyeBtn"
                          onClick={() => setShowPwd((v) => !v)}
                          aria-label={showPwd ? "Masquer" : "Afficher"}
                        >
                          {Icons.eye({ className: "i iSM" })}
                        </button>
                      }
                      required
                    />

                    <Field
                      label="Confirmer le mot de passe"
                      type={showPwd ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      icon={Icons.lock({ className: "i iSM" })}
                      required
                    />
                  </>
                ) : role === "MEDECIN" ? (
                  <>
                    <Field
                      label="Nom complet"
                      type="text"
                      placeholder="Dr Jean Dupont"
                      value={nomComplet}
                      onChange={(e) => setNomComplet(e.target.value)}
                      icon={Icons.user({ className: "i iSM" })}
                      required
                    />

                    <SelectField
                      label="Spécialité"
                      icon={Icons.stetho({ className: "i iSM" })}
                      value={specialite}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSpecialite(val);
                        if (val === "Psychologue") { setAgrement("PREFECTURE"); setAgrementConfirmed(false); }
                        else if (val === "Médecine générale") { setAgrement("ARS"); setAgrementConfirmed(false); }
                        else { setAgrement("ARS"); setAgrementConfirmed(false); }
                      }}
                    >
                      {SPECIALITES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </SelectField>

                    {specialite === "Médecine générale" ? (
                      <SelectField
                        label="Type d'agrément"
                        icon={Icons.stetho({ className: "i iSM" })}
                        value={agrement}
                        onChange={(e) => setAgrement(e.target.value)}
                      >
                        <option value="ARS">ARS</option>
                        <option value="PREFECTURE">Préfecture</option>
                        <option value="BOTH">ARS + Préfecture</option>
                      </SelectField>
                    ) : specialite === "Psychologue" ? (
                      <div className="field">
                        <div className="fieldLabel">Type d'agrément</div>
                        <label className={`checkboxAgree${agrementConfirmed ? " checkboxAgreeChecked" : ""}`}>
                          <input
                            type="checkbox"
                            checked={agrementConfirmed}
                            onChange={(e) => setAgrementConfirmed(e.target.checked)}
                          />
                          <span>Agréé Préfecture</span>
                        </label>
                      </div>
                    ) : (
                      <div className="field">
                        <div className="fieldLabel">Type d'agrément</div>
                        <label className={`checkboxAgree${agrementConfirmed ? " checkboxAgreeChecked" : ""}`}>
                          <input
                            type="checkbox"
                            checked={agrementConfirmed}
                            onChange={(e) => setAgrementConfirmed(e.target.checked)}
                          />
                          <span>Agréé ARS</span>
                        </label>
                      </div>
                    )}

                    <Field
                      label="Autorité d'agrément"
                      type="text"
                      placeholder="Ex: Agence Régionale de Santé"
                      value={autoriteAgrement}
                      onChange={(e) => setAutoriteAgrement(e.target.value)}
                      icon={Icons.user({ className: "i iSM" })}
                    />
                    <Field
                      label="RPPS"
                      type="text"
                      placeholder="Ex: 10001234567"
                      value={rpps}
                      onChange={(e) => setRpps(e.target.value)}
                      icon={Icons.user({ className: "i iSM" })}
                      required
                    />

                    <Field
                      label="Téléphone cabinet"
                      type="tel"
                      placeholder="01 23 45 67 89"
                      value={telephoneCabinet}
                      onChange={(e) => setTelephoneCabinet(e.target.value)}
                      icon={Icons.phone({ className: "i iSM" })}
                    />

                  <AddressAutocomplete
                    label="Adresse du cabinet"
                    icon={Icons.pin({ className: "i iSM" })}
                    value={adresseCabinet}
                    onChange={(e) => setAdresseCabinet(e.target.value)}
                    onSelect={({ street, postcode, city }) => {
                      setAdresseCabinet(street);
                      setCodePostalCabinet(postcode);
                      setVilleCabinet(city);
                    }}
                    placeholder="Ex : 10 rue du Faubourg, Paris"
                    required
                  />

                  <div className="rowFields">
                    <Field
                      label="Code postal"
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex: 75001"
                      value={codePostalCabinet}
                      onChange={(e) =>
                        setCodePostalCabinet(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      icon={Icons.pin({ className: "i iSM" })}
                      required
                    />
                    <Field
                      label="Ville"
                      type="text"
                      placeholder="Ex: Paris"
                      value={villeCabinet}
                      onChange={(e) => setVilleCabinet(e.target.value)}
                      icon={Icons.pin({ className: "i iSM" })}
                      required
                    />
                  </div>


                  </>
                ) : (
                  <>
                    <Field
                      label="Nom de la collectivité"
                      type="text"
                      placeholder="Ex: Ville de Paris"
                      value={nomCollectivite}
                      onChange={(e) => setNomCollectivite(e.target.value)}
                      icon={Icons.building({ className: "i iSM" })}
                      required
                    />

                  <AddressAutocomplete
                    label="Adresse de la collectivité"
                    icon={Icons.pin({ className: "i iSM" })}
                    value={adresseCollectivite}
                    onChange={(e) => setAdresseCollectivite(e.target.value)}
                    onSelect={({ street, postcode, city }) => {
                      setAdresseCollectivite(street);
                      setCodePostalCollectivite(postcode);
                      setVilleCollectivite(city);
                    }}
                    placeholder="Ex : 1 place de l'Hôtel de Ville, Paris"
                    required
                  />

                  <div className="rowFields">
                    <Field
                      label="Code postal"
                      type="text"
                      inputMode="numeric"
                      placeholder="75000"
                      value={codePostalCollectivite}
                      onChange={(e) =>
                        setCodePostalCollectivite(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      icon={Icons.pin({ className: "i iSM" })}
                      required
                    />
                    <Field
                      label="Ville"
                      type="text"
                      placeholder="Paris"
                      value={villeCollectivite}
                      onChange={(e) => setVilleCollectivite(e.target.value)}
                      icon={Icons.pin({ className: "i iSM" })}
                      required
                    />
                  </div>


                    <Field
                      label="Téléphone de la collectivité"
                      type="tel"
                      placeholder="01 23 45 67 89"
                      value={telephoneCollectivite}
                      onChange={(e) => setTelephoneCollectivite(e.target.value)}
                      icon={Icons.phone({ className: "i iSM" })}
                      required
                    />

                    <Field
                      label="SIRET"
                      type="text"
                      inputMode="numeric"
                      placeholder="14 chiffres"
                      value={siret}
                      onChange={(e) =>
                        setSiret(e.target.value.replace(/\D/g, "").slice(0, 14))
                      }
                      icon={Icons.building({ className: "i iSM" })}
                      required
                    />
                  </>
                )}

              <button
                type="submit"
                className="primaryBtn primaryBig"
                disabled={loading}
              >
                {loading ? "Création en cours..." : "Créer mon compte"}
              </button>

                <button
                  type="button"
                  className="superAdmin"
                  onClick={() => {
                    resetMessages();
                    resetRegisterFields();
                  }}
                >
                  Réinitialiser le formulaire
                </button>
              </form>
            )}

            {/* -------- VERIFY EMAIL -------- */}
            {mode === "register" && step === "VERIFY" && (
              <form className="form" onSubmit={handleSubmitVerify}>
                <div className="panelHeading" style={{ marginBottom: 8 }}>
                  Vérification email
                </div>

                <div style={{ marginBottom: 12, fontSize: 14 }}>
                  Un code a été envoyé à <b>{pendingEmail || email}</b>.
                  <br />
                  Saisissez-le pour terminer l’inscription.
                </div>

                <Field
                  label="Code de vérification"
                  type="text"
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  icon={Icons.lock({ className: "i iSM" })}
                  required
                />

                <button type="submit" className="primaryBtn primaryBig">
                  Valider le code
                </button>

                <button
                  type="button"
                  className="superAdmin"
                  onClick={() => {
                    setStep("FORM");
                    resetMessages();
                  }}
                >
                  ← Modifier mes informations
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
